import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { CheckCircle2, XCircle, Sparkles } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import ZButton from "@/components/ZButton";

/**
 * Magic-link landing page.
 * Reached via `/auth/magic/{token}`. Exchanges the token for tokens via the
 * backend, logs the user in via the AuthContext, and redirects to `next`.
 *
 * NOTE: The backend `/auth/magic-link/verify` endpoint is idempotent within a
 * 120-second window — React 18 StrictMode double-mounts are safe.
 */
export default function MagicLinkLandingPage() {
  const { token } = useParams();
  const nav = useNavigate();
  const { setUser } = useAuth();
  const [status, setStatus] = useState("loading"); // loading | ok | error
  const [error, setError] = useState("");
  const [claimed, setClaimed] = useState(null);
  const [next, setNext] = useState("/engagements");

  useEffect(() => {
    let alive = true;
    api.post(`/auth/magic-link/verify?token=${encodeURIComponent(token)}`)
      .then((r) => {
        if (!alive) return;
        if (setUser) setUser(r.data.user);
        setClaimed(r.data.claimed || null);
        setNext(r.data.next || "/engagements");
        setStatus("ok");
      })
      .catch((e) => {
        if (!alive) return;
        setError(errMsg(e));
        setStatus("error");
      });
    return () => { alive = false; };
  }, [token, setUser]);

  const claimedTotal = claimed
    ? Object.values(claimed).reduce((a, b) => a + b, 0)
    : 0;

  return (
    <div style={{ minHeight: "100vh", background: "#FAFAF7", display: "grid", placeItems: "center", padding: 20 }}>
      <div style={{ background: "white", borderRadius: 16, padding: 28, maxWidth: 460, width: "100%", boxShadow: "0 8px 28px rgba(0,0,0,0.08)", textAlign: "center" }}
           data-testid="magic-landing">
        {status === "loading" && (
          <>
            <Sparkles size={42} color="#DA7101" style={{ margin: "0 auto 14px" }} />
            <h1 style={{ fontSize: 20, fontWeight: 800, color: "#01696F", margin: 0 }}>Connexion en cours…</h1>
            <p style={{ color: "#5B6671", fontSize: 14, marginTop: 8 }}>Nous vérifions votre lien sécurisé.</p>
          </>
        )}
        {status === "ok" && (
          <>
            <CheckCircle2 size={48} color="#16A34A" style={{ margin: "0 auto 14px" }} />
            <h1 style={{ fontSize: 22, fontWeight: 800, color: "#01696F", margin: 0 }}>Bienvenue sur Zevents ! ✨</h1>
            <p style={{ color: "#5B6671", fontSize: 14, marginTop: 10 }}>
              Vous êtes connecté·e. Votre compte est désormais associé à toutes vos actions passées.
            </p>
            {claimedTotal > 0 && (
              <div style={{ background: "#F7F6F2", padding: 14, borderRadius: 12, marginTop: 14, textAlign: "left", fontSize: 13 }} data-testid="claimed-summary">
                <div style={{ fontWeight: 700, color: "#0F1418", marginBottom: 6 }}>{claimedTotal} action{claimedTotal > 1 ? "s" : ""} récupérée{claimedTotal > 1 ? "s" : ""} 🎉</div>
                {(claimed.tickets > 0) && <div>· {claimed.tickets} billet{claimed.tickets > 1 ? "s" : ""}</div>}
                {(claimed.gift_reservations > 0) && <div>· {claimed.gift_reservations} cadeau{claimed.gift_reservations > 1 ? "x" : ""}</div>}
                {(claimed.contributions > 0) && <div>· {claimed.contributions} contribution{claimed.contributions > 1 ? "s" : ""}</div>}
                {(claimed.vote_casts > 0) && <div>· {claimed.vote_casts} vote{claimed.vote_casts > 1 ? "s" : ""}</div>}
                {(claimed.survey_responses > 0) && <div>· {claimed.survey_responses} sondage{claimed.survey_responses > 1 ? "s" : ""}</div>}
                {(claimed.waitlist > 0) && <div>· {claimed.waitlist} liste{claimed.waitlist > 1 ? "s" : ""} d&apos;attente</div>}
              </div>
            )}
            <ZButton onClick={() => nav(next || "/engagements")} block style={{ marginTop: 18 }} data-testid="magic-continue-btn">
              Continuer
            </ZButton>
          </>
        )}
        {status === "error" && (
          <>
            <XCircle size={48} color="#DC2626" style={{ margin: "0 auto 14px" }} />
            <h1 style={{ fontSize: 20, fontWeight: 800, color: "#0F1418", margin: 0 }}>Lien invalide</h1>
            <p style={{ color: "#5B6671", fontSize: 14, marginTop: 10 }} data-testid="magic-error">{error}</p>
            <ZButton onClick={() => nav("/")} block style={{ marginTop: 18 }} variant="outline" data-testid="magic-home-btn">
              Retour à l&apos;accueil
            </ZButton>
          </>
        )}
      </div>
    </div>
  );
}
